import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import {
  getFanScanErrorMessage,
  getFanScanPayload,
  getFanScanSeo,
  isFanScanApiSuccess,
  isFanScanAuthError,
  updateFanScanSeo,
} from '../../services/fanScanApi';
import {
  SEO_DESCRIPTION_MAX,
  SEO_TITLE_MAX,
  buildSeoSaveBody,
  canonicalPathError,
  collectSeoErrors,
  formatUtcStamp,
  seoFromPayload,
  toSeoDraft,
} from './fanScanUtils';

function FanScanSeo() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  const [seo, setSeo] = useState(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getFanScanSeo().then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (isFanScanAuthError(res)) {
        window.location = '/login';
        return;
      }
      if (!isFanScanApiSuccess(res)) {
        setError(getFanScanErrorMessage(res, 'Failed to load landing SEO.'));
        return;
      }
      const next = seoFromPayload(getFanScanPayload(res));
      setSeo(next);
      setDraft(toSeoDraft(next));
      setError('');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    const errors = collectSeoErrors(draft);
    if (errors.length) {
      setFormError(errors.join(' '));
      setNotice('');
      return;
    }
    const body = buildSeoSaveBody(seo, draft);
    if (!body) {
      setFormError('');
      setNotice('Nothing changed.');
      return;
    }
    setSaving(true);
    setFormError('');
    setNotice('');
    const res = await updateFanScanSeo(body);
    setSaving(false);
    if (isFanScanAuthError(res)) {
      window.location = '/login';
      return;
    }
    if (!isFanScanApiSuccess(res)) {
      setFormError(getFanScanErrorMessage(res, 'Could not save landing SEO.'));
      return;
    }
    const saved = seoFromPayload(getFanScanPayload(res)) || { ...seo, ...body };
    setSeo(saved);
    setDraft(toSeoDraft(saved));
    setNotice('Saved. The landing page uses this title, description, and path.');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!draft) {
    return <Alert severity="error">{error || 'Landing SEO was not found.'}</Alert>;
  }

  const titleLength = String(draft.title ?? '').trim().length;
  const descriptionLength = String(draft.description ?? '').trim().length;
  const pathProblem = canonicalPathError(draft.canonical_path);

  return (
    <Paper sx={{ p: 2, maxWidth: 720 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This is the landing page only. It stays editable when the quiz bundle is approved. A player’s team or player name does not change these fields. Result pages keep their own SEO.
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {formError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formError}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {notice}
        </Alert>
      ) : null}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="SEO title"
          value={draft.title}
          onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
          inputProps={{ 'aria-label': 'SEO title', maxLength: SEO_TITLE_MAX }}
          helperText={`${titleLength} / ${SEO_TITLE_MAX}`}
          error={!String(draft.title || '').trim()}
        />
        <TextField
          label="SEO description"
          value={draft.description}
          multiline
          minRows={3}
          onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
          inputProps={{ 'aria-label': 'SEO description', maxLength: SEO_DESCRIPTION_MAX }}
          helperText={`${descriptionLength} / ${SEO_DESCRIPTION_MAX}`}
          error={!String(draft.description || '').trim()}
        />
        <TextField
          label="Canonical path"
          value={draft.canonical_path}
          onChange={(event) => setDraft((current) => ({ ...current, canonical_path: event.target.value }))}
          inputProps={{ 'aria-label': 'Canonical path' }}
          error={Boolean(pathProblem)}
          helperText={pathProblem || 'A path such as /fan-scan. The site adds its own origin. No host.'}
        />
        {seo?.updated_at ? (
          <Typography variant="caption" color="text.secondary">
            Last saved {formatUtcStamp(seo.updated_at)}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Not saved from admin yet.
          </Typography>
        )}
        <Box>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save SEO'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default FanScanSeo;
